import React from 'react';
import { cn } from '@/shared/utils';
import type { PopupArrowEdge, PopupArrowPlacement } from '@/shared/types/popup';
import { POPOVER_ARROW_SIDE_GAP_EXTRA_PX } from '../../utils/popupArrowPlacement';

export interface PopupArrowProps {
  placement: PopupArrowPlacement;
  className?: string;
  /**
   * Sin contorno en el trazo + sombra (mismo criterio que el panel con `showPanelBorder={false}`).
   * El relleno sigue usando el token de fondo del panel.
   */
  elevated?: boolean;
}

/**
 * Ancho horizontal del beak respecto al path base (`1` = ancho de diseño; menor = más estrecho).
 * Se aplica con `scale` en torno a x=14 (centro del viewBox).
 */
export const POPUP_ARROW_WIDTH_SCALE = 0.7;

/**
 * Inclinación fina del beak solo en bordes laterales (deg); 0 = perpendicular a `-90°` / `90°`.
 * Valores separados por si en diseño hace falta asimetría.
 */
export const POPUP_ARROW_TILT_LEFT_DEG = 3;
export const POPUP_ARROW_TILT_RIGHT_DEG = 3;

const arrowGroupTransform = `translate(14, 0) scale(${POPUP_ARROW_WIDTH_SCALE}, 1) translate(-14, 0)`;

/**
 * Relleno cerrado: la base no es un tramo recto (evita doble línea con el borde del panel),
 * se cierra con Q cóncava hacia arriba entre las esquinas inferiores.
 */
const ARROW_FILL_D =
  'M5.2 11.65 C4.35 11.65 3.85 10.95 4.25 10.15 C6.15 6.2 9.85 3.35 12.85 2.15 Q14 1.25 15.15 2.15 C18.15 3.35 21.85 6.2 23.75 10.15 C24.15 10.95 23.65 11.65 22.8 11.65 Q14 10.55 5.2 11.65 Z';

/** Solo laterales + punta: sin trazos en la base ni filetes sobre y=11.65. */
const ARROW_STROKE_D =
  'M4.25 10.15 C6.15 6.2 9.85 3.35 12.85 2.15 Q14 1.25 15.15 2.15 C18.15 3.35 21.85 6.2 23.75 10.15';

/**
 * Solapa el borde del panel (px). El solape va en `transform` + `calc`, no en top/left/bottom/right:
 * en varios navegadores esas propiedades redondean a píxeles enteros en el layout; `translate` mantiene subpíxeles.
 */
const BORDER_OVERLAP_PX = 2.3;

/**
 * Inset base hacia el interior del panel en bordes izq/der; `POPOVER_ARROW_SIDE_GAP_EXTRA_PX`
 * se resta para un ajuste fino siempre visible (ver utils).
 */
const POPUP_ARROW_SIDE_INSET_BASE_PX = 7;

function arrowWrapperStyle(edge: PopupArrowEdge, offsetPx: number): React.CSSProperties {
  const o = BORDER_OVERLAP_PX;
  const s = Math.max(0, POPUP_ARROW_SIDE_INSET_BASE_PX - POPOVER_ARROW_SIDE_GAP_EXTRA_PX);
  const tiltL = POPUP_ARROW_TILT_LEFT_DEG;
  const tiltR = POPUP_ARROW_TILT_RIGHT_DEG;
  const leftPx = `${offsetPx}px`;
  const topPx = `${offsetPx}px`;

  switch (edge) {
    case 'top':
      return {
        left: leftPx,
        top: 0,
        transform: `translate(-50%, calc(-100% + ${o}px))`,
      };
    case 'bottom':
      return {
        left: leftPx,
        bottom: 0,
        transform: `translate(-50%, calc(100% - ${o}px)) rotate(180deg)`,
      };
    case 'left':
      return {
        top: topPx,
        left: 0,
        transform: `translate(calc(-100% + ${o}px + ${s}px), -50%) rotate(${-90 + tiltL}deg)`,
      };
    case 'right':
      return {
        top: topPx,
        right: 0,
        transform: `translate(calc(100% - ${o}px - ${s}px), -50%) rotate(${90 - tiltR}deg)`,
      };
    default:
      return {};
  }
}

/** Aproximación a `ui-shadow-xl` del DS para el beak SVG (`filter` no admite los mismos tokens que `box-shadow`). */
const ELEVATED_ARROW_DROP_SHADOW =
  'drop-shadow(0px 8px 10px rgba(0, 0, 0, 0.10)) drop-shadow(0px 20px 25px rgba(0, 0, 0, 0.10))';

/**
 * Beak con punta suave: el borde del panel sigue recto; la flecha solo aporta trazo en los costados.
 */
export const PopupArrow = ({ placement, className, elevated = false }: PopupArrowProps) => {
  const { edge, offsetPx } = placement;

  return (
    <div
      className={cn('ui-pointer-events-none ui-absolute ui-z-[1]', className)}
      style={arrowWrapperStyle(edge, offsetPx)}
      aria-hidden
    >
      <svg
        width={28}
        height={12}
        viewBox="0 0 28 12"
        className="ui-block ui-shrink-0"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={elevated ? { filter: ELEVATED_ARROW_DROP_SHADOW } : undefined}
      >
        <g transform={arrowGroupTransform}>
          <path d={ARROW_FILL_D} fill="var(--ui-color-bg-fill-default)" />
          {elevated ? null : (
            <path
              d={ARROW_STROKE_D}
              fill="none"
              stroke="var(--ui-color-border-default)"
              strokeWidth={1}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </g>
      </svg>
    </div>
  );
};

PopupArrow.displayName = 'PopupArrow';
